import React from 'react'
import { getLink, getLinkFromRef, getMargins } from '../../utility'
import { HippostdHtml } from '../blog/blogArticle/componentTypes/HippostdHtml.jsx'
import { BrManageContentButton } from '@bloomreach/react-sdk'
import DOMPurify from 'dompurify'
import { ALIGNMENT, CardUtils } from '../cards/card.util.js'

import './clipcards.css'

const ClipCardsWrapper = ({ page, component }) => {
  const _parameters = component?.getParameters()

  const parameter = {
    columns: _parameters.columns || 0,
    marginTop: _parameters.marginTop || 'No Spacing',
    marginBottom: _parameters.marginBottom || 'No Spacing',
    alignment: _parameters.alignment || ALIGNMENT.LEFT,
    clipBackgroundColor: _parameters.clipBackgroundColor || 'Brand Dark',
    isScrollOnMobile: _parameters.isScrollOnMobile || false,
  }

  let items = []
  const _models = component.getModels()
  if (_models?.pagination) {
    const { pagination: _paginationRef } = _models
    const pagination = _paginationRef && page.getContent(_paginationRef)
    if (pagination) {
      const itemRefs = pagination?.getItems()
      const documents = itemRefs
        ?.map((itemRef) => itemRef && page.getContent(itemRef))
        ?.filter((doc) => !!doc)
      items = documents?.map((doc) => {
        const data = doc.getData()
        const bgImage = data?.bgImage
          ? page?.getContent(data?.bgImage)?.getOriginal()?.getUrl()
          : null

        return {
          id: data?.id,
          title: data?.title || '',
          body: data?.body?.html?.value || '',
          bgColor: data?.bgColor || '',
          bgImage: bgImage || '',
          bgImageOverlay: data?.bgImageOverlay || false,
          cardLink: data?.cardLink
            ? getLink(getLinkFromRef(data?.cardLink, page), null)
            : null,
          document: doc,
        }
      })
    }
  }

  const columns = parameter.columns || (items.length > 4 ? 4 : items.length)

  return (
    <div
      className={`ClipCards u-sizeConstrained ${getMargins(parameter.marginTop, parameter.marginBottom)}`}
    >
      <ClipCards
        items={items}
        columns={columns}
        clickable
        clipBackgroundColor={parameter.clipBackgroundColor}
        isScrollOnMobile={parameter.isScrollOnMobile}
      />
    </div>
  )
}

export default ClipCardsWrapper

export const ClipCards = ({
  items = [],
  columns = 0,
  clickable = false,
  clipBackgroundColor = 'Brand Dark',
  isScrollOnMobile = false,
}) => {
  return (
    <div
      className={`Grid Grid--withGutter ${isScrollOnMobile ? 'ClipCards--scrollOnMobile' : ''}`}
    >
      {items.map((item) => {
        const bgColor =
          item.bgColor && item.bgColor !== 'Default' ? item.bgColor : 'White'
        const background = CardUtils.getBackground(bgColor)
        const headingTextColor = CardUtils.getHeadingTextColor(bgColor)
        const border = CardUtils.getBorder(bgColor)
        const cardPadding = CardUtils.getCardPadding(bgColor, '4gu')
        const clipBgColor = CardUtils.getBackground(clipBackgroundColor)
        const bodyTextColor = ['Brand Dark', 'Brand', 'Blue Gradient'].includes(
          clipBackgroundColor
        )
          ? 'u-textColorWhite'
          : 'u-textColorSlateDark'
        const clipBorder =
          clipBackgroundColor === 'White' ? 'u-border u-borderColorNeutral' : ''

        return (
          <ClipCardDisplay
            key={item.id}
            data={{
              title: item.title,
              body: item.body,
              cardLink: item.cardLink,
              document: item.document,
            }}
            columns={columns}
            styleConfig={{
              headingTextColor,
              background,
              border,
              cardPadding,
              bgImage: item.bgImage || '',
              bgImageOverlay: item.bgImageOverlay || false,
              clipBgColor,
              bodyTextColor,
              clipBorder,
            }}
            clickable={clickable}
          />
        )
      })}
    </div>
  )
}

export const ClipCardDisplay = ({
  data = {},
  columns = 3,
  styleConfig = {},
  clickable = false,
}) => {
  const {
    headingTextColor = 'u-textColorBrandDark',
    background = '',
    border = '',
    cardPadding = '',
    bgImage = '',
    clipBgColor = 'u-bgColorBrandDark',
    bodyTextColor = 'u-textColorSlateDark',
    clipBorder = '',
    bgImageOverlay = false,
  } = styleConfig || {}

  const { title = '', body = '', cardLink = null, document = null } = data || {}

  const clickableLink = (clickable && cardLink) || null

  const CardWrapper = clickableLink
    ? ({ children, className }) => (
        <a
          href={DOMPurify.sanitize(clickableLink.url)}
          target={clickableLink.target || '_self'}
          rel={
            clickableLink.target === '_blank'
              ? 'noopener noreferrer'
              : undefined
          }
          className={className}
          data-analytics={clickableLink?.analyticsAttribute}
          style={{
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          {children}
        </a>
      )
    : ({ children, className }) => <div className={className}>{children}</div>

  return (
    <CardWrapper
      className={`u-flex u-flexCol u-sm-sizeFull u-md-size1of${columns} u-lg-size1of${columns} u-posRelative`}
    >
      {document && <BrManageContentButton content={document} />}
      <div
        className={`card u-flex u-flexCol ${background} ${bgImage && bgImageOverlay ? 'card-bg-overlay' : ''} ${border} ${clickableLink ? 'card-hover' : ''}`}
        style={
          bgImage
            ? {
                backgroundImage: `url(${bgImage})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center top',
              }
            : {}
        }
      >
        <div
          className={`card-content u-flex u-flexCol u-flexGrow1 u-flexJustifyCenter ${cardPadding}`}
        >
          {title && (
            <h3
              className={`heading u-textPrimary u-textSizeMsrp3 u-textBold ${headingTextColor}`}
            >
              {title}
            </h3>
          )}
          <div className={`card-body ${clipBgColor} ${clipBorder}`}>
            <HippostdHtml
              value={body}
              classNames={`${bodyTextColor} u-textPrimary u-textSizeMsrm1`}
            />
            {clickableLink && (
              <span
                className={`card-body-caret Icon--material ${bodyTextColor}`}
              >
                navigate_next
              </span>
            )}
          </div>
        </div>
      </div>
    </CardWrapper>
  )
}
